import { useMemo, useState } from 'react';
import {
  AI_SUGGEST_TONES,
  AI_TEMPLATE_CTA_STYLES,
  AI_TEMPLATE_GOALS,
  AI_TEMPLATE_LENGTHS,
  type TemplateCategory,
} from '@marketing-hub/shared';
import { Button } from '@/components/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/components/ui/Field';
import campStyles from '@/features/campaigns/Campaigns.module.css';

const GOAL_LABELS: Record<(typeof AI_TEMPLATE_GOALS)[number], string> = {
  meeting: 'Afspraak',
  reply: 'Antwoord',
  click: 'Klik / link',
  awareness: 'Bekendheid',
};

const LENGTH_LABELS: Record<(typeof AI_TEMPLATE_LENGTHS)[number], string> = {
  short: 'Kort',
  standard: 'Standaard',
};

const CTA_LABELS: Record<(typeof AI_TEMPLATE_CTA_STYLES)[number], string> = {
  soft_question: 'Zachte vraag',
  calendar: 'Kalender',
  link: 'Knop + link',
  none: 'Geen knop',
};

const TONE_LABELS: Record<(typeof AI_SUGGEST_TONES)[number], string> = {
  formal: 'Formeel',
  friendly: 'Vriendelijk',
  direct: 'Direct',
};

export type AiWizardOptions = {
  goal: (typeof AI_TEMPLATE_GOALS)[number];
  tone: (typeof AI_SUGGEST_TONES)[number];
  length: (typeof AI_TEMPLATE_LENGTHS)[number];
  cta_style: (typeof AI_TEMPLATE_CTA_STYLES)[number];
  user_brief: string;
  key_message?: string;
  cta_url?: string;
  calendar_link?: string;
};

type Props = {
  campaignType: TemplateCategory;
  onGenerate: (opts: AiWizardOptions) => void;
  isPending?: boolean;
  disabled?: boolean;
};

export function EmailTemplateAiWizard({ onGenerate, isPending, disabled }: Props) {
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState<(typeof AI_TEMPLATE_GOALS)[number]>('reply');
  const [tone, setTone] = useState<(typeof AI_SUGGEST_TONES)[number]>('friendly');
  const [length, setLength] = useState<(typeof AI_TEMPLATE_LENGTHS)[number]>('short');
  const [ctaStyle, setCtaStyle] =
    useState<(typeof AI_TEMPLATE_CTA_STYLES)[number]>('soft_question');
  const [userBrief, setUserBrief] = useState('');
  const [keyMessage, setKeyMessage] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [calendarLink, setCalendarLink] = useState('');

  const steps = ['Doel', 'Jouw input', 'Tone', 'Lengte', 'CTA', 'Controleren'];

  const briefOk = userBrief.trim().length >= 20;

  const validationHint = useMemo(() => {
    if (goal === 'click' && ctaStyle === 'link' && !ctaUrl.trim()) {
      return 'Vul een CTA-URL in (stap CTA of hieronder).';
    }
    if (goal === 'meeting' && ctaStyle === 'calendar' && !calendarLink.trim() && !userBrief.includes('http')) {
      return 'Vul een kalenderlink in of zet een URL in je brief.';
    }
    return null;
  }, [goal, ctaStyle, ctaUrl, calendarLink, userBrief]);

  function submit() {
    onGenerate({
      goal,
      tone,
      length,
      cta_style: ctaStyle,
      user_brief: userBrief.trim(),
      key_message: keyMessage.trim() || undefined,
      cta_url: ctaUrl.trim() || undefined,
      calendar_link: calendarLink.trim() || undefined,
    });
  }

  return (
    <div className={campStyles.aiWizard}>
      <p className={campStyles.aiWizardIntro}>
        AI bouwt alleen op basis van <strong>jouw tekst</strong> (brief + optionele velden). Geen
        verzonnen claims of URLs.
      </p>
      <div className={campStyles.aiWizardSteps} aria-label="AI stappen">
        {steps.map((label, i) => (
          <button
            key={label}
            type="button"
            className={[
              campStyles.aiWizardStep,
              i === step ? campStyles.aiWizardStepActive : '',
              i < step ? campStyles.aiWizardStepDone : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setStep(i)}
          >
            {i + 1}. {label}
          </button>
        ))}
      </div>

      <div className={campStyles.aiWizardPanel}>
        {step === 0 ? (
          <SelectInput value={goal} onChange={(e) => setGoal(e.target.value as typeof goal)}>
            {AI_TEMPLATE_GOALS.map((g) => (
              <option key={g} value={g}>
                {GOAL_LABELS[g]}
              </option>
            ))}
          </SelectInput>
        ) : null}

        {step === 1 ? (
          <div className={campStyles.aiWizardFormStack}>
            <Field
              label="Brief (verplicht)"
              hint="Wat bied je aan? Welke feiten, links of context moeten letterlijk terugkomen?"
            >
              <TextArea
                value={userBrief}
                onChange={(e) => setUserBrief(e.target.value)}
                rows={8}
                placeholder="Bijv.: We helpen loodgietersbedrijven in Zuid-Holland met meer offerte-aanvragen via Google Ads. Geen setup fee. Case: +40% leads in 3 maanden voor X. CTA: 15 min intake via …"
              />
            </Field>
            {!briefOk ? (
              <p className={campStyles.aiWizardWarn}>Minimaal 20 tekens in de brief.</p>
            ) : null}
            {(goal === 'reply' || goal === 'awareness') && (
              <Field label="Kernvraag / thema (optioneel)">
                <TextInput
                  value={keyMessage}
                  onChange={(e) => setKeyMessage(e.target.value)}
                  placeholder={
                    goal === 'awareness'
                      ? 'Welk nieuws of update deel je?'
                      : 'Welke ja/nee-vraag stel je?'
                  }
                />
              </Field>
            )}
            {goal === 'click' && (
              <Field label="Doel-URL (voor knop)" hint="Verplicht als CTA-stijl “Knop + link” is.">
                <TextInput
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  placeholder="https://…"
                />
              </Field>
            )}
            {goal === 'meeting' && (
              <Field label="Kalenderlink (optioneel)">
                <TextInput
                  value={calendarLink}
                  onChange={(e) => setCalendarLink(e.target.value)}
                  placeholder="https://calendly.com/…"
                />
              </Field>
            )}
          </div>
        ) : null}

        {step === 2 ? (
          <SelectInput value={tone} onChange={(e) => setTone(e.target.value as typeof tone)}>
            {AI_SUGGEST_TONES.map((t) => (
              <option key={t} value={t}>
                {TONE_LABELS[t]}
              </option>
            ))}
          </SelectInput>
        ) : null}
        {step === 3 ? (
          <SelectInput value={length} onChange={(e) => setLength(e.target.value as typeof length)}>
            {AI_TEMPLATE_LENGTHS.map((l) => (
              <option key={l} value={l}>
                {LENGTH_LABELS[l]}
              </option>
            ))}
          </SelectInput>
        ) : null}
        {step === 4 ? (
          <SelectInput
            value={ctaStyle}
            onChange={(e) => setCtaStyle(e.target.value as typeof ctaStyle)}
          >
            {AI_TEMPLATE_CTA_STYLES.map((c) => (
              <option key={c} value={c}>
                {CTA_LABELS[c]}
              </option>
            ))}
          </SelectInput>
        ) : null}
        {step === 5 ? (
          <div className={campStyles.aiWizardSummaryBlock}>
            <p className={campStyles.aiWizardSummary}>
              {GOAL_LABELS[goal]} · {TONE_LABELS[tone]} · {LENGTH_LABELS[length]} ·{' '}
              {CTA_LABELS[ctaStyle]}
            </p>
            <p className={campStyles.aiWizardBriefPreview}>
              {userBrief.trim().length > 280
                ? `${userBrief.trim().slice(0, 280)}…`
                : userBrief.trim()}
            </p>
            {validationHint ? (
              <p className={campStyles.aiWizardWarn}>{validationHint}</p>
            ) : null}
          </div>
        ) : null}

        <div className={campStyles.aiWizardNav}>
          <Button
            type="button"
            variant="ghost"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
          >
            Vorige
          </Button>
          {step < 5 ? (
            <Button
              type="button"
              disabled={step === 1 && !briefOk}
              onClick={() => setStep((s) => Math.min(5, s + 1))}
            >
              Volgende
            </Button>
          ) : (
            <Button
              type="button"
              disabled={disabled || isPending || !briefOk || Boolean(validationHint)}
              onClick={submit}
            >
              {isPending ? 'Genereren…' : 'Genereer template'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
